import React, { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { MapPin } from "@phosphor-icons/react";
import { loadGoogleMaps } from "@/lib/googleMaps";

/**
 * AddressAutocomplete — Textarea + Google Places suggestions dropdown.
 * Uses the modern `AutocompleteSuggestion.fetchAutocompleteSuggestions` API
 * (the legacy `AutocompleteService` is not available to API keys created
 * after March 2025).
 *
 * Props: value, onChange(string), placeholder, rows, className, dataTestId,
 * country (ISO2, default "JM").
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onLocationChange,
  placeholder = "Start typing an address...",
  rows = 2,
  className = "",
  dataTestId,
  country = "JM",
}) {
  const [ready, setReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const placesLibRef = useRef(null);   // google.maps.places namespace
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(async (google) => {
        if (cancelled) return;
        const places = await google.maps.importLibrary("places");
        if (cancelled) return;
        placesLibRef.current = places;
        sessionTokenRef.current = new places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch(() => setReady(false));
    return () => { cancelled = true; };
  }, []);

  // Re-fire predictions when ready flips true and there's already typed text
  useEffect(() => {
    if (ready && value && value.length >= 3) {
      fetchPredictions(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchPredictions = async (q) => {
    const places = placesLibRef.current;
    if (!places || !q || q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      const req = {
        input: q,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: country ? [country] : undefined,
      };
      const { suggestions: raw } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
      const list = (raw || [])
        .map((s) => s.placePrediction)
        .filter(Boolean)
        .slice(0, 5);
      setSuggestions(list);
      setOpen(list.length > 0);
    } catch (e) {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 250);
  };

  const pick = async (prediction) => {
    try {
      const place = prediction.toPlace();
      const fields = ["formattedAddress"];
      if (onLocationChange) fields.push("location");
      await place.fetchFields({ fields });
      const addr = place.formattedAddress || prediction.text?.toString() || "";
      onChange(addr);
      if (onLocationChange && place.location) {
        // place.location is a LatLng — call .lat() / .lng()
        try {
          const lat = typeof place.location.lat === "function" ? place.location.lat() : place.location.lat;
          const lng = typeof place.location.lng === "function" ? place.location.lng() : place.location.lng;
          if (typeof lat === "number" && typeof lng === "number") {
            onLocationChange(lat, lng);
          }
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      onChange(prediction.text?.toString() || "");
    } finally {
      // Rotate session token after a selection (Google billing best practice)
      if (placesLibRef.current) {
        sessionTokenRef.current = new placesLibRef.current.AutocompleteSessionToken();
      }
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <Textarea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        className={className}
        data-testid={dataTestId}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md shadow-lg max-h-64 overflow-auto"
          data-testid={`${dataTestId || "address"}-predictions`}
        >
          {suggestions.map((p, i) => {
            const main = p.mainText?.toString() || p.text?.toString() || "";
            const secondary = p.secondaryText?.toString() || "";
            return (
              <button
                type="button"
                key={p.placeId || i}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
                data-testid={`${dataTestId || "address"}-prediction-${i}`}
                className="w-full text-left px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 border-b border-zinc-100 dark:border-zinc-800 last:border-0 flex items-start gap-2"
              >
                <MapPin size={13} weight="bold" className="mt-0.5 text-zinc-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{main}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{secondary}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
